import {
    createSystem,
    defaultConfig,
    defineConfig,
    defineRecipe,
    defineSlotRecipe,
    defineTextStyles,
} from '@chakra-ui/react';
import { myAccountMenuRecipe } from './components/my-account-menu.recipe';
import { sectionHeadingRecipe } from './components/section-heading.recipe';
import { sidebarRecipe } from './components/sidebar.recipe';
import { tableRecipe } from './components/table.recipe';

export const textStyles = defineTextStyles({
    'body-1': {
        // 15/22
        value: {
            fontSize: '0.9375rem',
            fontWeight: '500',
            lineHeight: '1.375rem',
            fontFamily: 'body',
        },
    },
    'body-2': {
        // 14/21
        value: {
            fontSize: '0.875rem',
            fontWeight: '500',
            lineHeight: '1.3125rem',
            fontFamily: 'body',
        },
    },
    'body-3': {
        // 14/21
        value: {
            fontSize: '0.875rem',
            fontWeight: '400',
            lineHeight: '1.3125rem',
            fontFamily: 'body',
        },
    },
    'body-4': {
        // 13/19
        value: {
            fontSize: '0.8125rem',
            fontWeight: '500',
            lineHeight: '1.1875rem',
            fontFamily: 'body',
        },
    },
    'body-5': {
        // 13/19
        value: {
            fontSize: '0.8125rem',
            fontWeight: '400',
            lineHeight: '1.1875rem',
            fontFamily: 'body',
        },
    },
    'body-6': {
        // 12/18
        value: {
            fontSize: '0.75rem',
            fontWeight: '400',
            lineHeight: '1.125rem',
            fontFamily: 'body',
        },
    },
    'body-7': {
        // 22/22
        value: {
            fontSize: '1.375rem',
            fontWeight: '700',
            lineHeight: '1.375rem',
            fontFamily: 'mono',
        },
    },
    'body-8': {
        // 15/15
        value: {
            fontSize: '0.9375rem',
            fontWeight: '400',
            lineHeight: '0.9375rem',
            fontFamily: 'mono',
        },
    },
    'body-9': {
        // 13/13
        value: {
            fontSize: '0.8125rem',
            fontWeight: '400',
            lineHeight: '0.8125rem',
            fontFamily: 'mono',
        },
    },
    'body-table-name': {
        // 15/19
        value: {
            fontSize: '0.9375rem',
            fontWeight: '400',
            lineHeight: '1.1875rem',
            fontFamily: 'mono',
        },
    },
    'heading-1': {
        // 28/34
        value: {
            fontSize: '1.75rem',
            fontWeight: '500',
            lineHeight: '2.125rem',
            fontFamily: 'heading',
        },
    },
    'heading-2': {
        // 24/29
        value: {
            fontSize: '1.5rem',
            fontWeight: '500',
            lineHeight: '1.8125rem',
            fontFamily: 'heading',
        },
    },
    'heading-3': {
        // 20/24
        value: {
            fontSize: '1.25rem',
            fontWeight: '500',
            lineHeight: '1.5rem',
            fontFamily: 'heading',
        },
    },
    'heading-4': {
        // 18/22
        value: {
            fontSize: '1.125rem',
            fontWeight: '500',
            lineHeight: '1.375rem',
            fontFamily: 'heading',
        },
    },
    'heading-5': {
        // 16/19
        value: {
            fontSize: '1rem',
            fontWeight: '500',
            lineHeight: '1.1875rem',
            fontFamily: 'heading',
        },
    },
});

// Fallback stacks so a failed webfont load degrades to system fonts instead
// of the browser default (serif).
const SANS_FALLBACK =
    "system-ui, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif";
const MONO_FALLBACK =
    'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

const buttonRecipe = defineRecipe({
    variants: {
        // md (the stock 40px/14px page-level size) at 5px corners, matching
        // cloud-ui's lg page-header buttons; other sizes keep the stock radius.
        size: {
            md: { borderRadius: '5px' },
        },
        variant: {
            brand: {
                bg: 'blue.solid',
                color: 'blue.contrast',
                _hover: {
                    bg: 'color-mix(in srgb, {colors.blue.solid} 85%, black 15%)',
                },
                _dark: {
                    _hover: {
                        bg: 'color-mix(in srgb, {colors.blue.solid} 85%, white 15%)',
                    },
                },
            },
        },
    },
});

// Below `md`, every dialog renders exactly like Chakra's `size="full"`:
// these four declarations are the stock `full` variant's styles, applied to
// each size variant inside a max-width media query. At `md+` each variant's
// stock styles apply untouched.
// NOTE: a responsive `size` prop (size={{ base: 'full', md: 'lg' }}) is NOT
// equivalent — variant css cascades across breakpoint buckets and `full`
// would leak into desktop.
// Shared base for the slot recipes that default to the brand palette
// (checkbox, radioCard below). Kept as a hoisted const like
// dialogFullBelowMd: `slots: []` types `base` as Record<never, ...>, which
// rejects inline literals but accepts references.
const brandPaletteSlotBase = {
    root: { colorPalette: 'brand' },
};

// NOTE: applied per size variant, not recipe `base`: the merged variant
// object keeps the stock `maxW` key before this media-query block, so the
// override wins below md by source order; in `base` the order would flip.
const dialogFullBelowMd = {
    content: {
        // Form dialogs render `Content > form > (Body, Footer)`; the plain
        // <form> breaks the flex chain that lets Body (flex: 1) size against
        // the content box. Needed at every breakpoint: inside scroll caps the
        // content at md+ (body scrolls, footer visible), and the below-md
        // fullscreen sheet pins the footer the same way.
        '& > form': {
            display: 'flex',
            flexDirection: 'column',
            flex: '1',
            // Without this the form's automatic minimum (its full content)
            // stops it shrinking inside the capped dialog, and the footer
            // overflows past the panel instead of the body scrolling.
            minH: '0',
        },
        // Breakpoint conditions are registered WITHOUT the underscore prefix
        // (`mdDown`, not `_mdDown`) — an unknown `_mdDown` key would silently
        // no-op through the css pipeline.
        mdDown: {
            maxW: '100dvw',
            minH: '100dvh',
            // inside-scroll caps content at calc(100% - 7.5rem); minH would
            // out-rank it anyway, this keeps the fullscreen intent explicit.
            maxH: '100dvh',
            '--dialog-margin': '0',
            borderRadius: '0',
        },
    },
    footer: {
        mdDown: {
            // Stack action buttons full width in DOM order — Cancel first,
            // actions below — mirroring the desktop row order.
            flexDirection: 'column',
            alignItems: 'stretch',
            // Confirm CTAs carry long labels ("I understand, delete this
            // managed organization"); the stock button recipe pins
            // whiteSpace: nowrap + a fixed height, so an overlong label
            // paints past the sheet edges. Let stacked buttons wrap onto
            // extra lines instead.
            '& button': {
                whiteSpace: 'normal',
                height: 'auto',
                minH: '10',
                py: '2',
            },
        },
    },
};

export const libraryConfig = defineConfig({
    theme: {
        textStyles,
        tokens: {
            colors: {
                // The design-system gray ramp (@ionq/ui's --color-gray-*),
                // replacing Chakra's zinc so every gray in the shell — bg
                // steps, borders, muted ink, colorPalette='gray' fills —
                // matches the kit components and cloud-ui's pages. 950 is
                // left stock (the dark ladder below uses its own hexes).
                gray: {
                    50: { value: '#FAFAFA' },
                    100: { value: '#E9E9EB' },
                    200: { value: '#E0E0E3' },
                    300: { value: '#C0C0C3' },
                    400: { value: '#A0A0A3' },
                    500: { value: '#808083' },
                    600: { value: '#606063' },
                    700: { value: '#404043' },
                    800: { value: '#202023' },
                    900: { value: '#0F161B' },
                },
                // IonQ blue and purple, as full Chakra palettes so
                // colorPalette='blue'|'purple' and the palette semantics
                // (blue.solid, purple.fg, ...) carry the
                // design-system hues. Anchors: blue 100/500/600 (the kit's
                // ui-light-blue / ui-blue / ui-dark-blue) and purple
                // 100/500/600 are the design-system values; 400 is the
                // dark-mode ink grade; the other steps are derived to
                // match Chakra's ramp spacing.
                // Nested keys (not dotted) — {colors.…} references split
                // on dots.
                blue: {
                    50: { value: '#f2f6fe' },
                    100: { value: '#E6EEFD' },
                    200: { value: '#c4d8fb' },
                    300: { value: '#93bffe' },
                    400: { value: '#4c9ffe' },
                    500: { value: '#236BF6' },
                    600: { value: '#1F60DD' },
                    700: { value: '#194db0' },
                    800: { value: '#163b85' },
                    900: { value: '#122a5c' },
                    950: { value: '#0b1a3a' },
                },
                purple: {
                    50: { value: '#f7f5ff' },
                    100: { value: '#EFEBFF' },
                    200: { value: '#dcd4ff' },
                    300: { value: '#beb0ff' },
                    400: { value: '#9d88ff' },
                    500: { value: '#7B61FF' },
                    600: { value: '#5E3EFF' },
                    700: { value: '#4a2fd6' },
                    800: { value: '#3a25a6' },
                    900: { value: '#2b1c78' },
                    950: { value: '#1a1048' },
                },
                // IonQ brand (orange) ramp. 400/500/600 are the brand
                // bright/base/dark hexes; the remaining steps are derived
                // to match Chakra's stock ramp spacing. Makes
                // colorPalette='brand' a first-class palette (with the
                // semantic steps below).
                brand: {
                    50: { value: '#fff6e8' },
                    100: { value: '#feeacc' },
                    200: { value: '#fdd7a4' },
                    300: { value: '#fbba6b' },
                    400: { value: '#f89b33' },
                    500: { value: '#ed7c37' },
                    600: { value: '#de5126' },
                    700: { value: '#b83f1d' },
                    800: { value: '#93321a' },
                    900: { value: '#772b18' },
                    950: { value: '#41150a' },
                },
            },
            fonts: {
                heading: { value: `Colfax, ${SANS_FALLBACK}` },
                body: { value: `Colfax, ${SANS_FALLBACK}` },
                table: { value: `Colfax, ${SANS_FALLBACK}` },
                mono: { value: `'Ubuntu Mono', ${MONO_FALLBACK}` },
                tableBody: { value: '{fonts.mono}' },
            },
        },
        semanticTokens: {
            colors: {
                // Chakra built-in overrides — the whole app (built-in
                // components included) adopts the IonQ surface system.
                // Shape is load-bearing: createSystem deep-merges token
                // values, so overrides must use _light (not base) or the
                // defaults' surviving _light key wins in light mode, and
                // groups must use the nested DEFAULT form or sibling tokens
                // (bg.muted, bg.error, ...) get dropped from the merge.
                bg: {
                    // Dark surface ladder (one blue-gray family,
                    // darkest at the shell's edge): canvas #0d1218 <
                    // surface #141b22 < panel #1a222a < subtle #212a33 <
                    // muted #29333e < emphasized #303b47. subtle sits a
                    // clear step above panel so bands (table headers,
                    // header strips) read on cards, as they do in light.
                    // The rail sits on the darkest tone (bg.canvas); the
                    // top bar and the content panel on bg.page, a notch
                    // LIGHTER — the inverse of the old #0f161b-chrome /
                    // #111111-content near-tie.
                    DEFAULT: {
                        value: { _light: '{colors.white}', _dark: '#141b22' },
                    },
                    // In-content inset: one step off the page ground —
                    // darker in light, LIGHTER in dark (page header bands,
                    // table header bands, dialog wells, empty states).
                    subtle: {
                        value: {
                            _light: '#F4F4F5',
                            _dark: '#212a33',
                        },
                    },
                    // One step past subtle (stock consumers: table headers,
                    // striped rows) — the stock dark gray.900 #18181b is a
                    // neutral zinc that reads as a hole on the blue-gray
                    // ladder.
                    muted: {
                        value: {
                            _light: '{colors.gray.100}',
                            _dark: '#29333e',
                        },
                    },
                    // One step past muted (stock consumers: hover fills on
                    // muted surfaces, e.g. sortable table headers). The
                    // stock dark gray.800 #27272a is both off-family and
                    // DARKER than the retuned bg.muted — a hover that dims.
                    emphasized: {
                        value: {
                            _light: '{colors.gray.200}',
                            _dark: '#303b47',
                        },
                    },
                    // The rail's surface (sidebar recipe): white in light,
                    // the darkest surface in dark.
                    canvas: {
                        value: {
                            _light: '{colors.white}',
                            _dark: '#0d1218',
                        },
                    },
                    // The page ground beside the rail (top bar + content
                    // panel, see inset-shell.tsx): light gray in light, the
                    // surface tone in dark. The kit's page header
                    // (--shade-100) sits a notch darker in light — see
                    // styles/ionq-ui-dark.css.
                    page: {
                        value: {
                            _light: '{colors.gray.50}',
                            _dark: '#141b22',
                        },
                    },
                    // Popovers/menus float one step above the content
                    // surface (border + shadow do the rest).
                    panel: {
                        value: {
                            _light: '{colors.white}',
                            _dark: '#1a222a',
                        },
                    },
                    // Interaction tints for the chrome's rows, menu
                    // items and table rows. Translucent in dark so one
                    // token reads on every surface (the rail's canvas, the
                    // panel, a menu) — the opaque ladder steps above are
                    // resting surfaces, not feedback.
                    hover: {
                        value: {
                            _light: '{colors.gray.100}',
                            _dark: 'color-mix(in srgb, {colors.fg} 5%, transparent)',
                        },
                    },
                    active: {
                        value: {
                            _light: '{colors.gray.200}',
                            _dark: 'color-mix(in srgb, {colors.fg} 10%, transparent)',
                        },
                    },
                    // Status washes, light = the kit's *-light tints.
                    error: { value: { _light: '#FDF2F2' } },
                    success: { value: { _light: '#D6F1E7' } },
                    warning: { value: { _light: '#FFE5DB' } },
                    info: { value: { _light: '{colors.blue.100}' } },
                },
                fg: {
                    DEFAULT: {
                        value: {
                            _light: '{colors.gray.900}',
                            // Pinned hex (the pre-convergence gray.100): the
                            // dark ladder was tuned against it.
                            _dark: '#f4f4f5',
                        },
                    },
                    muted: {
                        value: {
                            _light: '{colors.gray.600}',
                            // Between gray.400 and gray.300: real hierarchy
                            // against fg (~2:1 apart) like GitHub/shadcn
                            // muted text, and 6.0:1 on the LIGHTEST band it
                            // sits on (bg.muted, the sortable table-header
                            // hover) — gray.400 only manages 4.9:1 there.
                            _dark: '#b0b0b8',
                        },
                    },
                    // Tertiary ink (placeholders, disabled labels, quiet
                    // icons). Stock light is gray.400 (2.6:1 on white); the
                    // design system's secondary text is gray.500 — the kit's
                    // --muted-foreground — which also clears 3:1 for the
                    // icons that use it. Dark keeps the stock gray.500 as a
                    // literal: gray.500 itself is the design-system ramp now.
                    subtle: {
                        value: {
                            _light: '{colors.gray.500}',
                            _dark: '#71717a',
                        },
                    },
                    // Interactive ink — links, menu actions, action icons.
                    // One grade lighter than the stock blue.fg (700/300),
                    // which stays untouched for palette components (an
                    // info Alert's text on blue.subtle).
                    link: {
                        value: {
                            _light: '{colors.blue.500}',
                            _dark: '{colors.blue.400}',
                        },
                    },
                    // The wordmark letters (mirrors the kit's --logo-text:
                    // a fixed mid gray in light, full ink in dark).
                    logo: {
                        value: {
                            _light: '#929293',
                            _dark: '{colors.fg}',
                        },
                    },
                    // Status ink, light = the design system's *-dark
                    // grades (kit --color-error-dark & co.): the base
                    // hues are fills — success #00AD6E is 2.9:1 on white
                    // — while these clear AA on white and on the bg.*
                    // washes below. Dark keeps Chakra's stock grades.
                    error: { value: { _light: '#CD1D1D' } },
                    success: { value: { _light: '#00764B' } },
                    warning: { value: { _light: '#BD3C00' } },
                    info: { value: { _light: '{colors.blue.600}' } },
                },
                border: {
                    DEFAULT: {
                        value: {
                            _light: '{colors.gray.200}',
                            // Opaque: the old 13% #e4e4e7 mix over the page
                            // ground. Translucent, each rule took the tone of
                            // whatever it sat on (bar vs header band) and
                            // adjacent lines read different.
                            _dark: '#2f353c',
                        },
                    },
                    muted: {
                        value: {
                            _light: '{colors.gray.100}',
                            _dark: 'color-mix(in srgb, #e4e4e7 7%, transparent)',
                        },
                    },
                    emphasized: {
                        value: {
                            _light: '{colors.gray.300}',
                            _dark: '#33404d',
                        },
                    },
                    // Stock dark is gray.950 (#111111): an opaque neutral
                    // hole next to the translucent siblings above.
                    subtle: {
                        value: {
                            _light: '{colors.gray.50}',
                            _dark: 'color-mix(in srgb, #e4e4e7 4%, transparent)',
                        },
                    },
                    // Status borders ARE the ink grades (3:1 non-text
                    // needs them: the base success hue is 2.9:1 on white).
                    error: { value: { _light: '{colors.fg.error}' } },
                    success: { value: { _light: '{colors.fg.success}' } },
                    warning: { value: { _light: '{colors.fg.warning}' } },
                    info: { value: { _light: '{colors.fg.info}' } },
                },

                // Global focus ring. Every stock recipe resolves its ring
                // color from colorPalette.focusRing, and the app-wide
                // default palette is gray (html { colorPalette: 'gray' } in
                // Chakra's stock globalCss) — so overriding gray.focusRing
                // recolors keyboard focus everywhere, including the recipes
                // that read the token directly (radiomark, tabs trigger,
                // slider thumb) and skip the focusRingColor utility.
                // Light uses brand.600 (≈3.9:1 on white) and dark brand.500
                // (≈6.2:1 on #141b22) to keep WCAG 2.2's 3:1 indicator
                // contrast in both modes.
                gray: {
                    focusRing: {
                        value: {
                            _light: '{colors.brand.600}',
                            _dark: '{colors.brand.500}',
                        },
                    },
                    // The gray palette's surface steps join the blue-gray
                    // dark ladder: the stock zinc fills (gray.900/800/700)
                    // read as off-family holes on it — avatar fallbacks,
                    // subtle badges, and every colorPalette='gray' fill.
                    // Light keeps the stock values.
                    subtle: {
                        value: {
                            _light: '{colors.gray.100}',
                            _dark: '#1c242c',
                        },
                    },
                    muted: {
                        value: {
                            _light: '{colors.gray.200}',
                            _dark: '#2b3642',
                        },
                    },
                    emphasized: {
                        value: {
                            _light: '{colors.gray.300}',
                            _dark: '#33404d',
                        },
                    },
                    // Outline buttons/badges/avatars read
                    // colorPalette.border under the default gray palette —
                    // without this the stock zinc gray.800 leaks back in
                    // dark. Light is the stock value (no-op).
                    border: {
                        value: {
                            _light: '{colors.gray.200}',
                            _dark: '#2b3642',
                        },
                    },
                },

                // Blue palette semantics. fg keeps the stock 700/300
                // (palette components — an info Alert's text on
                // blue.subtle — need that contrast); the interactive ink
                // is fg.link above. solid is the shell's blue fill: 500
                // light / 600 dark with the stock white contrast — the
                // pair the `brand` button variant paints, so a
                // colorPalette='blue' solid control matches the CTA.
                blue: {
                    solid: {
                        value: {
                            _light: '{colors.blue.500}',
                            _dark: '{colors.blue.600}',
                        },
                    },
                },
                // The legacy spend page's selected-tab highlight: the
                // design-system purple (600) in light instead of the stock
                // 700; 300 in dark so it reads on the ladder.
                purple: {
                    fg: {
                        value: {
                            _light: '{colors.purple.600}',
                            _dark: '{colors.purple.300}',
                        },
                    },
                },

                // Brand palette semantic steps — mirrors the stock orange
                // palette's light/dark mapping (including the asymmetric
                // solid: 600 light / 500 dark) so colorPalette='brand'
                // behaves like a first-class Chakra palette. focusRing
                // matches the gray.focusRing override above. border is
                // part of the stock palette contract too (avatar/badge/
                // alert read colorPalette.border) — omitting it would fall
                // through to the inherited gray palette's var.
                brand: {
                    contrast: {
                        value: {
                            _light: '{colors.white}',
                            _dark: '{colors.black}',
                        },
                    },
                    fg: {
                        value: {
                            _light: '{colors.brand.700}',
                            _dark: '{colors.brand.300}',
                        },
                    },
                    subtle: {
                        value: {
                            _light: '{colors.brand.100}',
                            _dark: '{colors.brand.900}',
                        },
                    },
                    muted: {
                        value: {
                            _light: '{colors.brand.200}',
                            _dark: '{colors.brand.800}',
                        },
                    },
                    emphasized: {
                        value: {
                            _light: '{colors.brand.300}',
                            _dark: '{colors.brand.700}',
                        },
                    },
                    solid: {
                        value: {
                            _light: '{colors.brand.600}',
                            _dark: '{colors.brand.500}',
                        },
                    },
                    focusRing: {
                        value: {
                            _light: '{colors.brand.600}',
                            _dark: '{colors.brand.500}',
                        },
                    },
                    border: {
                        value: {
                            _light: '{colors.brand.500}',
                            _dark: '{colors.brand.400}',
                        },
                    },
                    // The chrome's accent stroke — spinners, progress
                    // tracks, selected tabs,
                    // hover borders. Not a colorPalette step (nothing puts
                    // text on it): base orange in light, one grade darker
                    // in dark so it reads as orange, not peach, on the
                    // ladder.
                    accent: {
                        value: {
                            _light: '{colors.brand.500}',
                            _dark: '{colors.brand.600}',
                        },
                    },
                },
            },
        },
        slotRecipes: {
            accountMenu: myAccountMenuRecipe,
            sectionHeading: sectionHeadingRecipe,
            sidebar: sidebarRecipe,
            table: tableRecipe,
            // Deep-merged into the stock dialog recipe by createSystem's
            // mergeConfigs. `slots: []` is deliberate: mergeConfigs combines
            // arrays element-by-element, so a non-empty list would
            // positionally overwrite stock slot names.
            // defineSlotRecipe (vs a plain object) so defaultVariants can
            // reference the variant keys declared below type-wise.
            dialog: defineSlotRecipe({
                slots: [],
                // Chakra's "inside scroll": the body scrolls while header and
                // footer stay visible, instead of the whole dialog scrolling
                // in the positioner. App-wide default; a dialog can still opt
                // out with an explicit scrollBehavior='outside'.
                defaultVariants: {
                    scrollBehavior: 'inside',
                },
                variants: {
                    // Declared empty so `defaultVariants` above can reference
                    // the key type-wise; deep-merges as a no-op into the
                    // stock inside styles.
                    scrollBehavior: {
                        inside: {},
                    },
                    size: {
                        xs: dialogFullBelowMd,
                        sm: dialogFullBelowMd,
                        md: dialogFullBelowMd,
                        lg: dialogFullBelowMd,
                        xl: dialogFullBelowMd,
                    },
                },
            }),
            // Brand-tinted defaults for the controls where the checked/
            // selected state should read as IonQ orange. The palette lives
            // in the root slot's BASE STYLES (the pattern stock alert/
            // code-block use) — NOT in defaultVariants: colorPalette there
            // type-checks but the recipe compiler drops undeclared variant
            // keys, so it never reaches the DOM. From base it compiles to
            // the color-palette CSS vars on the root, which inherit to all
            // slots; an explicit colorPalette prop at a call site emits
            // unlayered element css that beats this @layer recipes rule,
            // and the stock recipes' own _invalid → red sits on the child
            // control, whose own vars beat the inherited ones — both still
            // win. `slots: []` for the same array-merge reason as the
            // dialog above.
            checkbox: defineSlotRecipe({
                slots: [],
                base: brandPaletteSlotBase,
            }),
            radioCard: defineSlotRecipe({
                slots: [],
                base: brandPaletteSlotBase,
            }),
            // Tabs scope the palette to the LIST slot, not root: Tabs.Content
            // (the whole page body under the strip) is a root descendant, and
            // color-palette CSS vars inherit — a root-level palette leaks
            // brand onto every neutral badge/button/chip inside the panels.
            // The list wraps only the triggers + indicator, exactly the
            // elements that should read brand. Content's own focus ring is
            // unaffected: it resolves the inherited gray palette, whose
            // focusRing is overridden to brand above.
            tabs: defineSlotRecipe({
                slots: [],
                // The enclosed list (jobs status filter): bg.muted in light
                // (subtle vanishes on the gray page), bg.subtle in dark.
                variants: {
                    variant: {
                        enclosed: {
                            list: {
                                bg: {
                                    base: '{colors.bg.muted}',
                                    _dark: '{colors.bg.subtle}',
                                },
                            },
                        },
                    },
                },
                base: {
                    list: { colorPalette: 'brand' },
                    // The stock palette resolves the selected trigger to
                    // brand.300 in dark — too pale to read as the accent.
                    // brand.accent keeps it a true dark orange.
                    trigger: {
                        _selected: {
                            _dark: { color: '{colors.brand.accent}' },
                        },
                    },
                },
            }),
        },
        recipes: {
            button: buttonRecipe,
            // Opaque fields: stock outline inputs are transparent, which
            // shows the shell's gray page ground through them.
            input: { variants: { variant: { outline: { bg: 'bg.panel' } } } },
            textarea: {
                variants: { variant: { outline: { bg: 'bg.panel' } } },
            },
        },
    },
    globalCss: {
        // Chakra's stock rule is `*::selection { bg: colorPalette
        // .emphasized/80 }`, and the default palette stays gray — so the
        // brand tint needs the literal token. The key must be exactly
        // '*::selection' so mergeConfigs replaces the stock value instead
        // of emitting a second equal-specificity rule whose winner would
        // depend on stylesheet order.
        '*::selection': {
            bg: 'brand.emphasized/80',
        },
    },
});

const librarySystem = createSystem(defaultConfig, libraryConfig);
// NOTE(nakardo): For running @chakra-ui/cli typegen script.
export default librarySystem;
